import { normalizeLivePortal, normalizePortalHistory } from "./liveAppPerception";

export const WHOLE_APP_WORLD_MODEL_VERSION = "whole-app-world-state-v1";
export const WHOLE_APP_TRANSITION_RULE_VERSION = "whole-app-journey-v1";



function clean(value, fallback = null, maxLength = 96) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_:/.-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, maxLength);
  return normalized || fallback;
}

// Requested chat/voice continuity is separate from optional journey analytics.
export function getWholeAppJourneySessionId() { return null; }

function dayPartForHour(hour) {
  if (hour >= 5 && hour < 12) return "morning";
  if (hour >= 12 && hour < 18) return "workday";
  if (hour >= 18 && hour < 23) return "evening";
  return "night";
}

function detectDevice() {
  if (typeof navigator === "undefined") return "unknown";
  const userAgent = navigator.userAgent || "";
  if (/ipad|iphone|ipod/i.test(userAgent)) return "ios";
  if (/android/i.test(userAgent)) return "android";
  return "desktop";
}

function detectBrowser() {
  if (typeof navigator === "undefined") return "unknown";
  const userAgent = navigator.userAgent || "";
  if (/firefox/i.test(userAgent)) return "firefox";
  if (/edg\//i.test(userAgent)) return "edge";
  if (/chrome|crios/i.test(userAgent)) return "chrome";
  if (/safari/i.test(userAgent) && !/chrome|crios/i.test(userAgent)) return "safari";
  return "other";
}

function inferAudience({ audience, goal, action } = {}) {
  const query = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const candidate = query?.get("audience") || query?.get("persona") || audience;
  const normalized = clean(candidate);
  if (["business", "recruiter", "explorer"].includes(normalized)) return normalized;

  const text = `${goal || ""} ${action || ""}`.toLowerCase();
  if (/recruit|hire|talent|business|value|roi|commercial/.test(text)) return "business";
  if (/skill|neuron|brain|spatial|ar|explore/.test(text)) return "explorer";
  return "general";
}

export function inferWholeAppGoal({ action, target, goal } = {}) {
  const explicit = clean(goal, null, 120);
  if (explicit) return explicit;

  const text = `${action || ""} ${target || ""}`.toLowerCase();
  if (/callback|contact|call|booking|email|message/.test(text)) return "connect_with_joz";
  if (/business|discover|value|roi/.test(text)) return "understand_business_value";
  if (/skill|mogg/.test(text)) return "explore_skills";
  if (/neuron|brain|maxx/.test(text)) return "explore_neurons";
  if (/ar|spatial|qr/.test(text)) return "launch_spatial_experience";
  if (/question|ask|chat|message/.test(text)) return "get_an_answer";
  if (/navigate|portal|route|state/.test(text)) return "explore_the_world";
  return "explore_the_world";
}

function pathPortal(path, fallback) {
  const match = String(path || "").match(/^\/neo\/([^/]+)/i);
  return clean(match?.[1] || fallback, "root");
}

function meshValue(mesh) {
  if (mesh && typeof mesh === "object") {
    return clean(mesh.id || mesh.name || mesh.mesh);
  }
  return clean(mesh);
}

export function buildWholeAppWorldState({ appState = {}, overrides = {} } = {}) {
  const source = appState && typeof appState === "object" ? appState : {};
  const path = overrides.path || source.currentPath || (typeof window !== "undefined" ? window.location.pathname : "/");
  const portal = pathPortal(path, overrides.portal || source.currentPortal);
  const mesh = meshValue(overrides.mesh ?? source.currentMesh);
  const stage = clean(overrides.stage ?? source.currentMeshStage);
  const phase = clean(overrides.phase ?? source.voiceState?.currentPhase);
  const uiState = source.uiState || {};
  const allowedActions = Array.isArray(overrides.allowedActions)
    ? overrides.allowedActions
    : Array.isArray(source.allowedActions)
      ? source.allowedActions
      : [];
  const safeAllowedActions = allowedActions
    .map((action) => clean(typeof action === "string" ? action : action?.type))
    .filter(Boolean)
    .slice(0, 32);
  const currentStateKey = clean(
    overrides.currentStateKey || source.currentStateKey || `${portal}:${mesh || "idle"}:${stage || "idle"}`,
    "root:idle",
    160
  );
  const historyCandidate = overrides.portalHistory
    || source.portalHistory
    || (typeof window !== "undefined" ? window.__liveAppContext?.portal_history : null);
  const portalHistory = normalizePortalHistory(historyCandidate);
  const goal = inferWholeAppGoal({
    action: overrides.action,
    target: overrides.target,
    goal: overrides.goal || source.userContext?.goal,
  });

  return {
    currentStateKey,
    portal,
    path: clean(path, "/", 180),
    mesh,
    stage,
    phase,
    goal,
    isMobile: Boolean(overrides.isMobile ?? uiState.isMobile),
    arSupported: Boolean(overrides.arSupported ?? uiState.arSupported),
    allowedActions: safeAllowedActions,
    portalHistory: [],
    modelBoundary: "app-state-and-user-journey-observation",
  };
}

function currentAppState() {
  if (typeof window === "undefined") return {};
  return window.__appState || {};
}

export async function recordWholeAppJourneyEvent({
  action,
  target = null,
  source = "app",
  outcomeType = "action_observed",
  goal = null,
  audience = null,
  appState = null,
  stateBefore = null,
  observedState = null,
  expectedEffects = [],
  observedEffects = [],
  success = true,
  requirePersistence = false,
} = {}) {
return null; // Visitor journey collection is retired.
}
